import { useEffect, useState } from "react";
import { Check, IndianRupee, DollarSign, Gift, ArrowRight, HeartHandshake } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal, Stagger, StaggerItem, AnimatedCounter } from "../lib/motion";

const openDonate = (amount) => window.dispatchEvent(new CustomEvent("open-donate", { detail: { amount } }));

const TIERS = [
  { key: "monthly-inr", icon: IndianRupee, label: "₹950", per: "/ month", note: "India · 80G benefit", amount: 950, highlight: false },
  { key: "monthly-usd", icon: DollarSign, label: "$10", per: "/ month", note: "Worldwide · USD", amount: 5000, highlight: true },
  { key: "one-time", icon: Gift, label: "One-time", per: "gift", note: "Any amount", amount: 2500, highlight: false },
];

const TRANSPARENCY = [
  "100% focused on educational impact",
  "Transparent fund utilisation, published quarterly",
  "Ground-level implementation with named schools",
  "Regular updates to every donor",
];

const MARQUEE = ["Akshara Daana", "Education for All", "Empowering Rural India", "Every Child, A Reader"];

export default function Donate() {
  const [stats, setStats] = useState({ visionaries_joined: 32, visionaries_target: 100 });
  const [fillWidth, setFillWidth] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/impact/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const pct = Math.round((stats.visionaries_joined / stats.visionaries_target) * 100);

  useEffect(() => {
    const t = setTimeout(() => setFillWidth(pct), 400);
    return () => clearTimeout(t);
  }, [pct]);

  async function submitVolunteer(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/volunteers", form);
      toast.success("Thank you — we will reach out with a personal note.");
      setForm({ name: "", email: "", phone: "", city: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="donate" data-testid="donate-section" className="relative py-16 md:py-32 bg-navy text-white overflow-hidden">
      {/* floating glowing orbs */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-teal/20 blur-[110px]" />
      <div className="pointer-events-none absolute bottom-0 -left-24 w-[26rem] h-[26rem] rounded-full bg-coral/15 blur-[110px]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <Reveal className="max-w-3xl text-center md:text-left">
          <span className="section-chip">Get Involved</span>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl md:text-6xl leading-[1.05] tracking-tight text-white">
            Join the first <span style={{ color: "#2563EB" }}>100 Visionaries.</span>
          </h2>
          <p className="mt-5 text-white/70 font-display text-base md:text-lg leading-relaxed">
            A founding circle of a hundred people committing a small, steady amount every month.
            That is how we will fund a decade of rural education — patiently, honestly, together.
          </p>
        </Reveal>

        {/* Progress + Tiers grid */}
        <div className="mt-14 grid lg:grid-cols-12 gap-6 items-stretch">
          {/* Progress card */}
          <Reveal className="lg:col-span-5" delay={0.05}>
            <div className="relative h-full manuscript-card manuscript-card-dark !bg-navy-soft border border-white/10 overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-teal/20 blur-3xl" />
              <div className="relative">
                <div className="text-teal font-display font-bold text-xs uppercase tracking-[0.2em]">Founding Circle · Progress</div>
                <div className="mt-4 flex items-end gap-2 flex-wrap">
                  <AnimatedCounter to={stats.visionaries_joined} className="stat-num text-5xl sm:text-6xl md:text-7xl text-white leading-none" format={(v) => v} />
                  <span className="stat-num text-3xl text-white/40 mb-1">/ {stats.visionaries_target}</span>
                </div>
                <div className="mt-1 font-display text-white/60 text-sm">visionaries joined so far</div>

                <div className="mt-7 flex items-center justify-between text-sm font-display">
                  <span className="text-white/50 uppercase tracking-widest text-xs font-bold">Goal</span>
                  <span data-testid="progress-percent" className="stat-num text-2xl text-sunflower">{pct}%</span>
                </div>
                <div className="mt-2 h-3 bg-white/10 rounded-full overflow-hidden">
                  <div
                    data-testid="progress-bar-fill"
                    className="h-full rounded-full bg-gradient-to-r from-teal via-teal to-coral transition-[width] duration-[1600ms] ease-out"
                    style={{ width: `${fillWidth}%` }}
                  />
                </div>

                <button
                  data-testid="donate-open-progress"
                  onClick={() => openDonate(950)}
                  className="btn-vermilion w-full justify-center mt-8 group"
                >
                  <HeartHandshake className="w-4 h-4" /> Become a Visionary
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </Reveal>

          {/* Tiers */}
          <Stagger className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-5 min-w-0" amount={0.25}>
            {TIERS.map((t) => {
              const Icon = t.icon;
              return (
                <StaggerItem key={t.key} className="h-full">
                  <div
                    data-testid={`donate-tier-${t.key}`}
                    className={`group relative h-full flex flex-col rounded-[0.6rem] p-6 border transition-transform duration-300 hover:-translate-y-1.5 ${
                      t.highlight
                        ? "bg-teal text-white border-teal shadow-teal"
                        : "bg-white text-navy border-white/10 shadow-card hover:shadow-2xl"
                    }`}
                  >
                    {t.highlight && (
                      <div className="absolute -top-3 right-5 bg-coral text-white text-[10px] uppercase font-display font-bold tracking-widest px-3 py-1 rounded-full">
                        Most Chosen
                      </div>
                    )}
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${t.highlight ? "bg-white/20 text-white" : "bg-teal/10 text-teal"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="mt-5 flex items-baseline gap-1">
                      <span className="stat-num text-3xl leading-none">{t.label}</span>
                      <span className={`font-display text-sm ${t.highlight ? "text-white/70" : "text-navy/50"}`}>{t.per}</span>
                    </div>
                    <div className={`mt-1 text-[11px] uppercase tracking-widest font-display font-bold ${t.highlight ? "text-white/80" : "text-navy/50"}`}>{t.note}</div>
                    <button
                      data-testid={`donate-choose-${t.key}`}
                      onClick={() => openDonate(t.amount)}
                      className={`mt-auto pt-6 inline-flex items-center gap-2 font-display font-bold text-sm ${t.highlight ? "text-white" : "text-teal"}`}
                    >
                      Choose this
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>

        {/* Editorial marquee */}
        <div className="mt-16 border-y border-white/10 py-5 overflow-hidden w-full max-w-full">
          <div className="marquee-track items-center">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex items-center shrink-0" aria-hidden={dup === 1}>
                {MARQUEE.map((m, i) => (
                  <span key={`${dup}-${i}`} className="flex items-center whitespace-nowrap">
                    <span className="font-display font-extrabold text-xl sm:text-2xl md:text-3xl tracking-tight text-white/85 px-4 sm:px-6">{m}</span>
                    <span className="text-coral text-2xl px-1">✦</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Transparency + Volunteer form */}
        <div id="volunteer" className="mt-16 grid md:grid-cols-2 gap-6 items-start">
          <Reveal>
            <div className="manuscript-card !bg-white text-navy h-full">
              <h3 className="font-display font-extrabold text-navy text-2xl">Our promise</h3>
              <ul className="mt-5 space-y-3.5">
                {TRANSPARENCY.map((t) => (
                  <li key={t} className="flex items-start gap-3 font-display text-navy/75 text-sm md:text-base">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-teal text-white flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <motion.form
              onSubmit={submitVolunteer}
              data-testid="volunteer-form"
              className="manuscript-card manuscript-card-dark !bg-navy-soft border border-white/10 h-full"
            >
              <h3 className="font-display font-extrabold text-2xl text-white">Volunteer / Join the circle</h3>
              <p className="text-white/60 font-display text-sm mt-1.5">
                Share your details and we will reach back with a personal note.
              </p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="vol-name" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 text-white placeholder-white/40 px-3 py-2.5 rounded-sm font-display text-sm border border-white/15 focus:outline-none focus:border-teal transition-colors" />
                <input data-testid="vol-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 text-white placeholder-white/40 px-3 py-2.5 rounded-sm font-display text-sm border border-white/15 focus:outline-none focus:border-teal transition-colors" />
                <input data-testid="vol-phone" required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/5 text-white placeholder-white/40 px-3 py-2.5 rounded-sm font-display text-sm border border-white/15 focus:outline-none focus:border-teal transition-colors" />
                <input data-testid="vol-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-white/5 text-white placeholder-white/40 px-3 py-2.5 rounded-sm font-display text-sm border border-white/15 focus:outline-none focus:border-teal transition-colors" />
                <textarea data-testid="vol-message" placeholder="Why you'd like to join" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="sm:col-span-2 bg-white/5 text-white placeholder-white/40 px-3 py-2.5 rounded-sm font-display text-sm border border-white/15 focus:outline-none focus:border-teal transition-colors" />
              </div>
              <button data-testid="vol-submit" type="submit" disabled={submitting} className="mt-5 btn-coral disabled:opacity-60">
                {submitting ? "Sending…" : "Send my details"}
              </button>
            </motion.form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
